// src/pages/PrivacyPolicy.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  MdOutlinePrivacyTip,
  MdSecurity,
  MdAnalytics,
  MdStorage,
  MdDeleteForever,
  MdGavel,
  MdAlternateEmail,
  MdChildFriendly,
  MdPolicy,
} from "react-icons/md";

export default function PrivacyPolicy(): React.ReactElement {
  return (
    <main className="min-h-screen bg-app text-onSurface">
      <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 py-10 md:px-6 lg:px-8">
        {/* HERO / RESUMEN */}
        <section className="rounded-2xl border border-surface/40 bg-surface/60 p-6 shadow-sm backdrop-blur-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                <MdOutlinePrivacyTip className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold md:text-2xl">
                  Política de privacidad de RADAR 4 – App móvil
                </h1>
                <p className="mt-1 text-sm opacity-80">
                  Esta política explica qué datos se recogen en la aplicación
                  móvil RADAR 4 (iOS y Android), con qué fines, cómo se
                  almacenan y protegen, y cómo puedes ejercer tus derechos de
                  acceso o eliminación de cuenta y datos.
                </p>
                <p className="mt-2 text-xs uppercase tracking-wide opacity-60">
                  Última actualización: 1 de diciembre de 2025
                </p>
              </div>
            </div>

            {/* Chips tipo “privacy label” */}
            <div className="mt-2 flex flex-wrap gap-2 md:mt-0 md:justify-end">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                <MdSecurity className="h-4 w-4" />
                Datos cifrados en tránsito
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 px-3 py-1 text-xs font-medium text-secondary">
                <MdAnalytics className="h-4 w-4" />
                Analítica agregada
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-surfaceVariant/60 px-3 py-1 text-xs font-medium text-onSurfaceVariant">
                <MdGavel className="h-4 w-4" />
                Sin venta de datos personales
              </span>
            </div>
          </div>

          {/* <p className="mt-4 text-xs leading-relaxed opacity-70">
            Esta página está pensada para cumplir los requisitos de
            transparencia de la App Store y Google Play, pero no sustituye el
            asesoramiento legal profesional. El Operador de RADAR 4 debe revisar
            y adaptar este texto a la legislación aplicable.
          </p> */}
        </section>

        {/* 0. ÁMBITO */}
        <section className="card relative overflow-hidden rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdOutlinePrivacyTip className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              0. Ámbito de aplicación
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Esta política se aplica al tratamiento de datos personales que se
            realiza a través de la <strong>aplicación móvil RADAR 4</strong>,
            utilizada para crear y gestionar evaluaciones de sostenibilidad,
            cursos, comunidad y otras funciones relacionadas con el modelo RADAR
            4.
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            La app móvil se conecta a servicios en la nube (“backend”) donde se
            almacenan las evaluaciones y demás información necesaria para su
            funcionamiento. Esos servicios forman parte de la misma solución
            técnica, pero el presente documento está centrado en lo que ocurre
            desde la perspectiva de la app que instalas en tu dispositivo.
          </p>
        </section>

        {/* 1. RESPONSABLE */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdGavel className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              1. Responsable del tratamiento
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            RADAR 4 es un sistema integral de evaluación de la sostenibilidad
            que permite analizar el desempeño de entidades en dimensiones
            ambiental, social, económica y de gobernanza ética y participativa.
          </p>
          <p className="mt-2 text-sm leading-relaxed opacity-90">
            El responsable del tratamiento de tus datos personales será la
            organización, institución o empresa que te haya dado acceso a la app
            RADAR 4 (en adelante, <strong>“el Operador”</strong>). El
            desarrollador de la app presta la plataforma tecnológica, mientras
            que el Operador decide cómo se usa en su proyecto concreto.
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Cuando RADAR 4 se distribuya directamente por el propio
            desarrollador, podrás contactar para cuestiones de privacidad en el
            correo{" "}
            <a href="mailto:radar4sostenibilidad@gmail.com" className="link">
              radar4sostenibilidad@gmail.com
            </a>
            , además de los canales que se indiquen en la ficha de la app.
          </p>
        </section>

        {/* 2. QUÉ DATOS SE RECOGEN */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdStorage className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              2. Qué datos recoge la app RADAR 4
            </h2>
          </div>
          <p className="mb-2 text-sm leading-relaxed opacity-90">
            En la aplicación móvil RADAR 4 se tratan principalmente los
            siguientes tipos de datos:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>
              <strong>Datos de cuenta y autenticación:</strong> dirección de
              correo electrónico, nombre mostrado, organización a la que
              perteneces y, en su caso, información básica de perfil (rol,
              permisos de acceso). Estos datos se usan para crear tu cuenta y
              permitirte acceder a funciones que son necesariamente
              cuenta-basadas (evaluaciones, cursos, comunidad, etc.).
            </li>
            <li>
              <strong>Datos de evaluaciones:</strong> información de las
              entidades evaluadas, respuestas a cuestionarios, comentarios
              internos, documentación de soporte y resultados asociados al
              modelo RADAR 4.
            </li>
            <li>
              <strong>
                Datos de cursos y formación (si están habilitados):
              </strong>{" "}
              progreso en contenidos, cuestionarios, resultados, certificados y
              cualquier interacción relacionada con módulos formativos dentro de
              la app.
            </li>
            <li>
              <strong>Contenido de comunidad (si está habilitada):</strong>{" "}
              publicaciones, comentarios, reacciones, métricas básicas de uso
              (por ejemplo, número de reacciones o comentarios) y metadatos
              asociados a moderación.
            </li>
            <li>
              <strong>Archivos y evidencias:</strong> documentos, imágenes o
              vídeos que se suban como evidencias de evaluación o recursos de
              comunidad. Estos archivos se almacenan en servicios seguros de
              almacenamiento en la nube asociados a la app.
            </li>
            <li>
              <strong>Datos de analítica y uso:</strong> eventos de navegación
              dentro de la app, tiempo de uso, pantallas visitadas y acciones
              relevantes (por ejemplo, crear una evaluación, completar un
              cuestionario, publicar contenido), recopilados de forma
              preferentemente agregada para entender cómo se utiliza la app y
              mejorarla.
            </li>
            <li>
              <strong>Datos técnicos del dispositivo:</strong> tipo de
              dispositivo, sistema operativo, versión de la app, idioma y
              errores técnicos registrados en logs para diagnóstico y seguridad
              (por ejemplo, cierres inesperados o fallos de red).
            </li>
            <li>
              <strong>Datos de soporte:</strong> información que nos
              proporciones al contactar con el soporte de la app o del proyecto
              (mensajes, adjuntos, datos de contacto).
            </li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            El contenido concreto de las evaluaciones, la comunidad y los cursos
            dependerá del proyecto de sostenibilidad que el Operador desarrolle
            con RADAR 4.
          </p>
        </section>

        {/* 3. FINES DEL TRATAMIENTO */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdAnalytics className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              3. Para qué se utilizan estos datos
            </h2>
          </div>
          <p className="mb-2 text-sm leading-relaxed opacity-90">
            Los datos anteriores se utilizan con las siguientes finalidades:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>
              <strong>Gestión de acceso y seguridad:</strong> permitir que las
              personas autorizadas inicien sesión, gestionar sesiones activas,
              aplicar control de roles y permisos, y prevenir accesos no
              autorizados o usos indebidos. RADAR 4 es una app orientada a
              usuarios autenticados porque todas sus funciones principales se
              asocian a una cuenta y a una organización.
            </li>
            <li>
              <strong>Operación del modelo RADAR 4:</strong> crear, editar y
              gestionar evaluaciones; almacenar respuestas y evidencias;
              calcular indicadores y generar visualizaciones y reportes
              relacionados con la sostenibilidad de las entidades evaluadas.
            </li>
            <li>
              <strong>Gestión de cursos y matrículas (si aplica):</strong>{" "}
              administrar contenidos formativos, registrar tu progreso,
              cuestionarios, resultados y certificados.
            </li>
            <li>
              <strong>Gestión de comunidad (si aplica):</strong> habilitar
              publicaciones, comentarios y reacciones; fomentar el intercambio
              de buenas prácticas y moderar el espacio para mantener un entorno
              seguro.
            </li>
            <li>
              <strong>Analítica y mejora continua:</strong> analizar de forma
              agregada el uso de la app (a partir de Analytics y logs) para
              mejorar la usabilidad, el rendimiento, la estabilidad técnica y la
              calidad del modelo de evaluación y de la experiencia de usuario.
            </li>
            <li>
              <strong>Cumplimiento legal y auditoría:</strong> mantener
              registros necesarios para auditorías internas o externas, para
              demostrar la trazabilidad de los resultados y cumplir obligaciones
              legales o contractuales del Operador.
            </li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            RADAR 4 no está diseñada para mostrar anuncios de terceros ni para
            utilizar tus datos con fines de publicidad basada en el seguimiento
            entre aplicaciones. Si en el futuro esto cambiara, se actualizaría
            esta política y, cuando sea necesario, se solicitaría tu
            consentimiento explícito.
          </p>
        </section>

        {/* 4. ALMACENAMIENTO Y PROTECCIÓN */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdSecurity className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              4. Cómo se almacenan y protegen tus datos
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            La app RADAR 4 utiliza servicios en la nube de proveedores
            especializados (por ejemplo, plataformas tipo Firebase para
            autenticación, base de datos, almacenamiento de archivos, analítica
            y registro de errores) para operar de forma segura y escalable.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>
              <strong>Cifrado en tránsito:</strong> la comunicación entre la app
              y los servidores se realiza mediante conexiones seguras (HTTPS),
              lo que ayuda a proteger la información frente a interceptaciones.
            </li>
            <li>
              <strong>Control de acceso por rol:</strong> se aplican reglas de
              seguridad y permisos a nivel de back-end para limitar quién puede
              ver o modificar cada tipo de dato según su función (por ejemplo,
              evaluador, auditor, administrador de organización).
            </li>
            <li>
              <strong>Segregación de proyectos:</strong> cada cliente o
              implementación de RADAR 4 se gestiona en un contexto lógico
              separado, de forma que los datos de diferentes organizaciones no
              se mezclen.
            </li>
            <li>
              <strong>Registros y auditoría técnica:</strong> se generan logs de
              eventos relevantes (errores, accesos, acciones sensibles) para
              detectar incidentes de seguridad, diagnosticar problemas y aplicar
              medidas correctivas.
            </li>
            <li>
              <strong>Buenas prácticas de desarrollo seguro:</strong> la app se
              desarrolla siguiendo pautas para reducir riesgos como inyección de
              código, accesos no autorizados o exposición innecesaria de datos.
            </li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Ningún sistema es completamente invulnerable. En caso de incidente
            de seguridad que afecte a tus datos, el Operador adoptará las
            medidas oportunas e informará conforme a la normativa aplicable.
          </p>
        </section>

        {/* 5. ALMACENAMIENTO LOCAL (APP MÓVIL) */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdStorage className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              5. Almacenamiento local en tu dispositivo
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            La app RADAR 4 puede utilizar almacenamiento local en tu dispositivo
            (por ejemplo, mecanismos similares a bases de datos locales o
            almacenamiento de clave-valor) para:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>Mantener tu sesión iniciada de forma segura.</li>
            <li>
              Guardar temporalmente información necesaria para el funcionamiento
              offline limitado o para recuperar acciones si se pierde la
              conexión.
            </li>
            <li>Recordar ciertas preferencias de interfaz o idioma.</li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Estos datos permanecen en tu dispositivo y se eliminan cuando
            desinstalas la app o borras los datos de la aplicación desde los
            ajustes del sistema operativo, sin perjuicio de la información que
            ya se haya sincronizado con el servidor.
          </p>
        </section>

        {/* 6. TERCEROS, SDKs Y TRANSFERENCIAS */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdPolicy className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              6. Servicios de terceros, SDKs y transferencias internacionales
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Para funcionar, RADAR 4 integra bibliotecas y SDKs de terceros,
            principalmente con fines de autenticación, almacenamiento, analítica
            y registro de errores (por ejemplo, servicios en la nube como
            Firebase u otras plataformas equivalentes). Estos proveedores actúan
            normalmente como encargados del tratamiento, siguiendo las
            instrucciones del Operador.
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>
              <strong>Compartición con terceros:</strong> tus datos personales
              no se venden a terceros. Solo se comparten con proveedores
              necesarios para prestar el servicio, con socios institucionales
              del proyecto en forma agregada o anonimizada, o cuando exista una
              obligación legal.
            </li>
            <li>
              <strong>Transferencias internacionales:</strong> algunos
              proveedores pueden estar ubicados en otros países. En esos casos,
              el Operador procurará que existan garantías adecuadas de
              protección (por ejemplo, cláusulas contractuales tipo u otros
              mecanismos reconocidos por la normativa aplicable).
            </li>
            <li>
              <strong>Servicios de inteligencia artificial:</strong> la app
              RADAR 4 no envía tus datos personales a servicios de IA de
              terceros con fines publicitarios o de perfilado masivo. Si en el
              futuro se integran herramientas de IA que traten datos personales,
              se informará de forma clara y, cuando sea necesario, se recabará
              tu consentimiento explícito.
            </li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            En la fecha de esta actualización, RADAR 4 utiliza, entre otros, los
            servicios de backend, autenticación, base de datos, almacenamiento,
            analítica y registro de errores proporcionados por plataformas tipo
            Firebase. La lista concreta de proveedores puede evolucionar con el
            tiempo y se actualizará en esta política cuando sea relevante.
          </p>
        </section>

        {/* 7. DERECHOS Y ELIMINACIÓN */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdDeleteForever className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              7. Cómo acceder, rectificar o eliminar tus datos y tu cuenta
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            De acuerdo con la legislación de protección de datos que corresponda
            (por ejemplo, Reglamento General de Protección de Datos –RGPD– o
            leyes locales de protección de datos personales), puedes ejercer tus
            derechos de:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>Acceso a los datos personales que se tratan sobre ti.</li>
            <li>
              Rectificación de datos inexactos o incompletos almacenados en
              relación con tu cuenta y actividad en RADAR 4.
            </li>
            <li>
              Supresión o eliminación de tus datos y cuenta cuando ya no sean
              necesarios para las finalidades descritas o cuando retires tu
              consentimiento (en la medida en que sea legalmente posible).
            </li>
            <li>
              Limitación u oposición al tratamiento en determinadas
              circunstancias.
            </li>
            <li>Portabilidad de los datos, cuando sea legalmente aplicable.</li>
          </ul>
          <p className="mt-2 text-sm leading-relaxed opacity-90">
            Puedes iniciar una solicitud de acceso, rectificación o eliminación
            de tu cuenta y datos personales escribiendo al correo{" "}
            <a href="mailto:radar4sostenibilidad@gmail.com" className="link">
              radar4sostenibilidad@gmail.com
            </a>
            . Para tu seguridad, te pediremos que utilices, siempre que sea
            posible, la misma dirección de correo con la que te registraste en
            la app, o que acredites tu identidad de forma equivalente.
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Ten en cuenta que la eliminación de tu cuenta puede no implicar la
            eliminación inmediata de toda la información cuando exista una
            obligación legal o un interés legítimo en conservar determinados
            datos durante más tiempo (por ejemplo, para auditoría o defensa de
            reclamaciones). Estas solicitudes también pueden canalizarse a
            través de la organización que actúe como Operador del proyecto.
          </p>
          <div className="mt-4 flex flex-col gap-2 rounded-xl border border-surfaceVariant/60 bg-surfaceVariant/20 px-3 py-3 text-xs leading-relaxed">
            <div className="flex items-center gap-2">
              <MdDeleteForever className="h-4 w-4 text-error" />
              <span className="font-semibold">
                Eliminación de cuenta desde la web (Delete Account URL)
              </span>
            </div>
            <p className="opacity-80">
              Si ya no tienes la app instalada o prefieres iniciar la
              eliminación desde el navegador, puedes utilizar la página
              específica de eliminación de cuenta de RADAR 4. Desde esa página
              podrás encontrar las instrucciones para solicitar el borrado de tu
              cuenta y de tus datos personales:
            </p>
            <p className="font-medium">
              <Link to="/delete-account" className="link">
                https://radar-4.web.app/delete-account
              </Link>
            </p>
          </div>
        </section>

        {/* 8. CONSERVACIÓN */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdStorage className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              8. Plazos de conservación
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Tus datos se conservarán mientras:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm opacity-90">
            <li>Tu cuenta de usuario permanezca activa en RADAR 4.</li>
            <li>
              Existan evaluaciones, procesos de certificación, cursos o
              actividades de comunidad vinculadas a tu participación que
              requieran mantener la trazabilidad de la información.
            </li>
            <li>
              Sea necesario para cumplir obligaciones legales, contractuales o
              de auditoría impuestas al Operador.
            </li>
          </ul>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Una vez finalizados los plazos anteriores, los datos podrán ser
            anonimizados o eliminados siguiendo las políticas internas del
            Operador y la normativa aplicable.
          </p>
        </section>

        {/* 9. MENORES DE EDAD */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdChildFriendly className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              9. Menores de edad
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            RADAR 4 no está dirigida de forma específica a menores de edad como
            usuarios finales independientes. Si en algún proyecto de
            sostenibilidad se tratan datos de menores mediante la app, el
            Operador será responsable de garantizar que dicha participación se
            realiza conforme a la normativa de protección de datos aplicable,
            obteniendo cuando proceda el consentimiento de madres, padres o
            tutores.
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Si tienes motivos para pensar que se están tratando datos de menores
            sin las garantías adecuadas, ponte en contacto con el Operador o con
            el desarrollador de RADAR 4 a través del correo{" "}
            <a href="mailto:radar4sostenibilidad@gmail.com" className="link">
              radar4sostenibilidad@gmail.com
            </a>
            .
          </p>
        </section>

        {/* 10. CONTACTO Y AVISO LEGAL */}
        <section className="card rounded-2xl border border-surface/30 bg-surface/80 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <MdAlternateEmail className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold md:text-lg">
              10. Información de contacto y avisos finales
            </h2>
          </div>
          <p className="text-sm leading-relaxed opacity-90">
            Para consultas generales sobre privacidad relacionadas con la app
            RADAR 4 publicada por el desarrollador, puedes escribir a{" "}
            <a href="mailto:radar4sostenibilidad@gmail.com" className="link">
              radar4sostenibilidad@gmail.com
            </a>
            . En los proyectos donde exista un Operador específico (por ejemplo,
            una universidad, empresa o administración pública), también podrás
            utilizar los canales de contacto que dicha entidad haya comunicado.
          </p>
          <p className="mt-2 text-xs leading-relaxed opacity-70">
            Este texto es un modelo informativo general y debe ser revisado y
            adaptado por el equipo legal responsable del proyecto, para
            alinearse con la normativa específica aplicable en tu país o región
            (por ejemplo, RGPD, CCPA, Ley Federal de Protección de Datos
            Personales en Posesión de los Particulares u otras). Las categorías
            de datos descritas aquí están alineadas con la información que se
            muestra en la ficha de la app en la App Store y Google Play.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <Link to="/" className="link">
              Volver al inicio
            </Link>
            <span className="opacity-40">•</span>
            <Link to="/login" className="link">
              Ir a la página de acceso
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
